import * as i0 from '@angular/core';
import { EventEmitter, Directive, Output, NgModule } from '@angular/core';
import { fromEvent, Subject } from 'rxjs';
import { debounceTime, map, filter, takeUntil } from 'rxjs/operators';

function findScrollContainer(element) {
    if (!element || !window || !window.getComputedStyle)
        return null;
    const style = window.getComputedStyle(element);
    if (style.overflow === 'auto' || style.overflowY === 'auto')
        return element;
    return findScrollContainer(element.parentElement);
}

const BOTTOM_THRESHOLD = 200; // px
function getDocHeight(doc) {
    return Math.max(doc.body.scrollHeight, doc.documentElement.scrollHeight, doc.body.offsetHeight, doc.documentElement.offsetHeight, doc.body.clientHeight, doc.documentElement.clientHeight);
}
function createWindowObservable(win) {
    return fromEvent(win, 'scroll').pipe(debounceTime(100), map(() => ({
        position: (document.documentElement.scrollTop ||
            document.body.scrollTop) + window.innerHeight,
        bottom: getDocHeight(win.document) - BOTTOM_THRESHOLD,
    })));
}
function createContainerObservable(container) {
    return fromEvent(container, 'scroll').pipe(debounceTime(100), map(() => ({
        position: container.scrollTop,
        bottom: (container.scrollHeight -
            container.clientHeight -
            BOTTOM_THRESHOLD),
    })));
}
class OnScrollToBottomDirective {
    constructor(element, zone) {
        this.element = element;
        this.zone = zone;
        this.onScrollToBottom = new EventEmitter();
        this.destroy$ = new Subject();
    }
    ngAfterViewInit() {
        this.zone.runOutsideAngular(() => {
            setTimeout(() => {
                const container = findScrollContainer(this.element.nativeElement);
                const scroll$ = container
                    ? createContainerObservable(container)
                    : createWindowObservable(window);
                scroll$
                    .pipe(filter(({ position, bottom }) => position >= bottom), // we're at the bottom!
                takeUntil(this.destroy$))
                    .subscribe(() => this.zone.run(() => this.onScrollToBottom.emit()));
            }, 100);
        });
    }
    ngOnDestroy() {
        this.destroy$.next();
        this.destroy$.complete();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.10", ngImport: i0, type: OnScrollToBottomDirective, deps: [{ token: i0.ElementRef }, { token: i0.NgZone }], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "18.2.10", type: OnScrollToBottomDirective, selector: "[onScrollToBottom]", outputs: { onScrollToBottom: "onScrollToBottom" }, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.10", ngImport: i0, type: OnScrollToBottomDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[onScrollToBottom]', // tslint:disable-line:directive-selector
                }]
        }], ctorParameters: () => [{ type: i0.ElementRef }, { type: i0.NgZone }], propDecorators: { onScrollToBottom: [{
                type: Output
            }] } });
class ScrollToBottomModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.10", ngImport: i0, type: ScrollToBottomModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.2.10", ngImport: i0, type: ScrollToBottomModule, declarations: [OnScrollToBottomDirective], exports: [OnScrollToBottomDirective] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.2.10", ngImport: i0, type: ScrollToBottomModule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.10", ngImport: i0, type: ScrollToBottomModule, decorators: [{
            type: NgModule,
            args: [{
                    exports: [OnScrollToBottomDirective],
                    declarations: [OnScrollToBottomDirective],
                }]
        }] });

function scrollTo(options = {}) {
    const delay = options.delay ?? 0;
    const smooth = options.smooth ?? false;
    const offset = options.offset ?? 100;
    return new Promise(resolve => {
        setTimeout(() => {
            const container = options.container ?? findScrollContainer(options.element) ?? window;
            let top = 0;
            if (options.element) {
                top =
                    options.element.offsetTop -
                        (container.offsetTop ?? 0) -
                        offset;
            }
            if (isNaN(top))
                top = 0;
            if (top !== (container.scrollTop ?? 0)) {
                try {
                    container.scrollTo({
                        top,
                        behavior: smooth ? 'smooth' : 'auto',
                    });
                }
                catch (error) {
                    console.warn('scrollTo failed', error);
                    if (container instanceof HTMLElement)
                        container.scrollTop = top;
                }
            }
            resolve();
        }, Math.max(0, delay));
    });
}

/**
 * Generated bundle index. Do not edit.
 */

export { OnScrollToBottomDirective, ScrollToBottomModule, findScrollContainer, scrollTo };
